import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';

export interface StepConfig {
  label: string;
  icon: React.ReactNode;
  description?: string;
  summary?: string; // Dynamic summary like "2 services selected"
}

export interface StepWizardProps {
  currentStep: number;
  totalSteps: number;
  steps: StepConfig[];
  onStepClick?: (step: number) => void;
  allowStepClick?: boolean;
  className?: string;
}

// Default step icons
const defaultIcons = {
  user: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  briefcase: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  location: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  chart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  document: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  payment: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  check: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ),
};

const StepWizard: React.FC<StepWizardProps> = ({
  currentStep,
  totalSteps,
  steps,
  onStepClick,
  allowStepClick = false,
  className,
}) => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const percentage = (currentStep / totalSteps) * 100;

  const handleStepClick = (stepNumber: number) => {
    // Only allow clicking on completed steps or current step
    if (allowStepClick && stepNumber <= currentStep && onStepClick) {
      onStepClick(stepNumber);
    }
  };

  const toggleStepPreview = (stepNumber: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedStep(expandedStep === stepNumber ? null : stepNumber);
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Mobile View - Compact with current step highlighted */}
      <div className="md:hidden">
        {/* Progress bar */}
        <div className="relative mb-4">
          <div className="h-1.5 bg-secondary-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          
          {/* Step dots on progress bar */}
          <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-0">
            {steps.map((_, index) => {
              const stepNumber = index + 1;
              const isCompleted = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              
              return (
                <motion.div
                  key={stepNumber}
                  className={cn(
                    'w-3 h-3 rounded-full border-2 transition-all',
                    isCompleted && 'bg-primary-500 border-primary-500',
                    isCurrent && 'bg-white border-primary-500 ring-4 ring-primary-100',
                    !isCompleted && !isCurrent && 'bg-white border-secondary-300'
                  )}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: isCurrent ? 1.2 : 1 }}
                  transition={{ duration: 0.2 }}
                />
              );
            })}
          </div>
        </div>

        {/* Current Step Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-xl p-4 border border-primary-200"
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary-500/30">
              {steps[currentStep - 1]?.icon || defaultIcons.document}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                  Step {currentStep}/{totalSteps}
                </span>
              </div>
              <h3 className="text-base font-semibold text-secondary-900 mt-1 truncate">
                {steps[currentStep - 1]?.label}
              </h3>
              {steps[currentStep - 1]?.description && (
                <p className="text-xs text-secondary-600 mt-0.5 line-clamp-1">
                  {steps[currentStep - 1].description}
                </p>
              )}
            </div>
          </div>

          {/* Step Navigation Pills */}
          <div className="flex gap-1.5 mt-4 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {steps.map((step, index) => {
              const stepNumber = index + 1;
              const isCompleted = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              
              return (
                <button
                  key={stepNumber}
                  onClick={() => handleStepClick(stepNumber)}
                  disabled={!allowStepClick || stepNumber > currentStep}
                  className={cn(
                    'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                    'flex items-center gap-1.5 min-h-[32px]',
                    isCompleted && 'bg-primary-500 text-white',
                    isCurrent && 'bg-white text-primary-700 ring-2 ring-primary-500',
                    !isCompleted && !isCurrent && 'bg-secondary-100 text-secondary-500',
                    allowStepClick && stepNumber <= currentStep && 'cursor-pointer hover:opacity-80',
                    (!allowStepClick || stepNumber > currentStep) && 'cursor-default'
                  )}
                >
                  {isCompleted ? (
                    <span className="w-4 h-4 flex items-center justify-center">
                      {defaultIcons.check}
                    </span>
                  ) : (
                    <span>{stepNumber}</span>
                  )}
                  <span className="hidden xs:inline">{step.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Completed Steps Summary (collapsible) */}
        {currentStep > 1 && (
          <div className="mt-3">
            <button
              onClick={(e) => toggleStepPreview(-1, e)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {currentStep - 1} step{currentStep > 2 ? 's' : ''} completed
              </span>
              <motion.svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ rotate: expandedStep === -1 ? 180 : 0 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </button>
            
            <AnimatePresence>
              {expandedStep === -1 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 pt-2 px-1">
                    {steps.slice(0, currentStep - 1).map((step, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-2 bg-green-50 rounded-lg border border-green-100"
                      >
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white">
                          {defaultIcons.check}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-secondary-900 truncate">{step.label}</p>
                          {step.summary && (
                            <p className="text-xs text-green-600">{step.summary}</p>
                          )}
                        </div>
                        {allowStepClick && (
                          <button
                            onClick={() => handleStepClick(index + 1)}
                            className="text-xs text-primary-600 font-medium hover:underline"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Desktop View - Horizontal steps with full labels */}
      <div className="hidden md:block">
        {/* Progress bar with step indicators */}
        <div className="relative">
          <div className="h-2 bg-secondary-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          
          {/* Step circles on progress line */}
          <div className="absolute top-0 left-0 w-full h-2 flex items-center">
            {steps.map((_, index) => {
              const stepNumber = index + 1;
              const isCompleted = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              const position = ((stepNumber) / totalSteps) * 100;
              
              return (
                <motion.button
                  key={stepNumber}
                  onClick={() => handleStepClick(stepNumber)}
                  disabled={!allowStepClick || stepNumber > currentStep}
                  className={cn(
                    'absolute transform -translate-x-1/2 transition-all duration-300',
                    'h-8 w-8 rounded-full border-2 flex items-center justify-center',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                    isCompleted && 'bg-primary-500 border-primary-500 text-white',
                    isCurrent && 'bg-white border-primary-500 text-primary-600 ring-4 ring-primary-100',
                    !isCompleted && !isCurrent && 'bg-white border-secondary-300 text-secondary-400',
                    allowStepClick && stepNumber <= currentStep && 'cursor-pointer hover:scale-110',
                    (!allowStepClick || stepNumber > currentStep) && 'cursor-default'
                  )}
                  style={{ left: `${position}%` }}
                  whileHover={allowStepClick && stepNumber <= currentStep ? { scale: 1.1 } : {}}
                  whileTap={allowStepClick && stepNumber <= currentStep ? { scale: 0.95 } : {}}
                >
                  {isCompleted ? (
                    defaultIcons.check
                  ) : (
                    <span className="text-sm font-semibold">{stepNumber}</span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
        
        {/* Step labels below progress bar */}
        <div className="flex mt-6 justify-between">
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber < currentStep;
            const isCurrent = stepNumber === currentStep;
            
            return (
              <div
                key={stepNumber}
                className={cn(
                  'flex flex-col items-center text-center',
                  'w-0 flex-1'
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-xl mb-2 transition-all',
                    isCompleted && 'bg-green-100 text-green-600',
                    isCurrent && 'bg-primary-100 text-primary-600',
                    !isCompleted && !isCurrent && 'bg-secondary-100 text-secondary-400'
                  )}
                >
                  {step.icon || defaultIcons.document}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium max-w-[100px] leading-tight',
                    isCompleted && 'text-green-600',
                    isCurrent && 'text-primary-600',
                    !isCompleted && !isCurrent && 'text-secondary-500'
                  )}
                >
                  {step.label}
                </span>
                {step.summary && isCompleted && (
                  <span className="text-[10px] text-green-500 mt-0.5 max-w-[100px] truncate">
                    {step.summary}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Export default icons for use in parent components
export { defaultIcons as StepIcons };
export { StepWizard };
